(ns hai.web-app.add-menu
  "Task add modal.

  Mobile drives an add bottom-sheet (hai.home.mobile.add-sheet) whose task
  tile opens the modal defined here, as does the desktop universal
  \"Add anything…\" bar; note creation goes through the expanded editor
  (hai.notes.create) on every surface."
  (:require
   [hai.entity.draft :as draft]
   [hai.tasks.assignee-picker :as assignee-picker]
   [hai.tasks.data :as tasks-data]
   [hai.tasks.form :as form]
   [hai.tasks.header :as header]
   [hai.tasks.mini-app :as tasks-mini-app]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent defsub]]
   [hai.web-app.routing :as routing]
   [lambdaisland.ornament :as o]))

;; ---------------------------------------------------------------------------
;; Task events
;;
;; Task creation reuses hai.tasks.data/create, which persists via the
;; task/create resolver and prepends to :hai.tasks.data/list — the same
;; key the Desktop card subscribes to and reloads on every visit. So a task
;; added from here shows up wherever tasks are shown, no extra refresh.
;; ---------------------------------------------------------------------------

(def draft-id
  "The draft behind the ＋ menu's Add-a-task modal."
  ::draft)

(defsub ::task-modal-open? [db]
  (get db ::task-modal-open? false))

;; `title` seeds the composer for a host that already asked for one — the
;; universal bar hands over whatever was typed into it, and none of it is filed,
;; so the modal's Save is there from the first render.
(defevent ::open-task-modal [db title]
  (-> db
      (assoc ::task-modal-open? true ::task-modal-title title)
      (draft/seed draft-id (form/from-task {:task/title title}
                                           (tasks-data/default-urgency db)))))

;; The draft goes with the modal: reopening always starts fresh.
(defevent ::close-task-modal [db _]
  (-> db
      (dissoc ::task-modal-open? ::task-modal-title)
      (draft/discard draft-id)))

;; Cancel empties the composer back to the title the modal opened on; ✕ closes.
(defevent ::reset-task-modal [db _]
  (draft/seed db draft-id (form/from-task {:task/title (::task-modal-title db)}
                                          (tasks-data/default-urgency db))))

;; ---------------------------------------------------------------------------
;; Task modal
;; ---------------------------------------------------------------------------

;; Radix wires the dialog's accessible name and description to its own Title
;; and Description; the visible title is the card's header bar, so both are
;; hidden here rather than rendered twice.
(def ^:private visually-hidden
  {:position "absolute" :width "1px" :height "1px" :padding "0" :margin "-1px"
   :overflow "hidden" :clip "rect(0 0 0 0)" :white-space "nowrap" :border "0"})

;; The mini-app card is the modal's surface, so the dialog panel underneath it
;; keeps only its size — no padding and no background of its own. `align=start`
;; top-anchors the dialog (so it grows downward without recentering); the
;; margin-top sits it a comfortable way below the viewport top rather than the
;; ~32px `--space-6` gap that alignment leaves on its own. It overrides only the
;; top of Radix's `margin: auto`, so the dialog stays horizontally centred.
(def ^:private bare-dialog-panel
  {:padding "0" :background "transparent" :box-shadow "none"
   :margin-top "10vh"})

;; The duck-tape strip pins a card to the dashboard. A modal floats over the
;; page instead, so it sheds the tape and keeps the rest of the card.
(o/defstyled modal-card tasks-mini-app/card
  [:&::before {:display "none"}])

(def ^:private close-icon (icons/mui-icon :outlined :close))

(defn task-modal []
  (let [open? @(<s [::task-modal-open?])
        saving? (= :start @(<s [:fetch/state ::create]))
        default-urgency @(<s [::tasks-data/default-urgency])
        close! (fn [] (>e [::close-task-modal]))]
    [c/DialogRoot
     {:open open?
      :onOpenChange (fn [opening?]
                      (when-not opening?
                        (close!)))}
     ;; Top-anchored (not vertically centered), so growing from the collapsed
       ;; title field to the full composer on the first keystroke expands the
       ;; dialog downward instead of recentering it — no jump.
       [c/DialogContent {:align "start" :max-width "480px" :style bare-dialog-panel}
      [c/DialogTitle {:style visually-hidden} "Add a task"]
      [c/DialogDescription {:style visually-hidden} "Create a new task."]
        ;; Mounted only while open, over a draft opening the modal seeds. No
        ;; assignee is staged: an unpicked composer means the creator, same as
        ;; everywhere.
      (when open?
        [modal-card
         [header/bar {:title "Add a task"}
          [:button.bar-action-btn
           {:type "button"
            :aria-label "Close"
            :title "Close"
            :on-click close!}
           [close-icon {:size 18}]]]
         [form/task-form
          {:show-hint? true
           :draft-id draft-id
           :default-urgency default-urgency
           :saving? saving?
           :on-save (fn [task]
                      (>e [::tasks-data/create (assoc task
                                                      :request-id ::create
                                                      :task-link {:href-fn #(routing/href :tasks/detail {:task/uuid %})
                                                                    :target routing/link-target}
                                                        :success-fx [[:dispatch [::close-task-modal]]])]))
           :on-cancel #(>e [::reset-task-modal])
           :on-close close!
           :render-assignee-picker
           (fn [{:keys [on-pick trigger]}]
             [assignee-picker/component
              {:editable? true
               :on-pick on-pick
               :label "Assign"
               :trigger trigger}])}]])]]))

(defn modals []
  [:<>
   [task-modal]])
